import { useState } from 'react'

import { type InfiniteData, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import { getStoryBackground } from 'components/UI/BackgroundPicker/BackgroundPicker'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { PaymentModal, PostGoal } from 'components-ref/molecules'
import { Modal } from 'components-ref/molecules/modal/modal'
import { usePreviewContext } from 'context/previewContext'
import { usePostActions } from 'helpers/helperHooks'
import { useTipPost } from 'helpers/hooks'
// import { pagedPostEngageMutationOptions } from 'helpers/mutationOptions'
import { getCachedBlob } from 'helpers/mediaPrefetch'
import { useUserProfile } from 'store'
import { type IInfinitePage, type ITipPayload } from 'types/interfaces/ITypes'

import { type IProps } from './PostGoalWrapper.types'

const PostGoalWrapper = ({ dataQuery, postData, postIndex, role }: IProps) => {
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const userData = useUserProfile()

    /*     const { engagePost } = useEngagePost(
        postData.id,
        postData.is_engage,
        pagedPostEngageMutationOptions(queryClient, dataQuery)
    ) */
    const [selectedTipValue, setSelectedTipValue] = useState(0)
    const previewData = usePreviewContext()
    const { onTipMutate: updatePostTipCache } = usePostActions(
        postData.id,
        dataQuery
    )
    const [confirmBuyModalOpen, setConfirmBuyModalOpen] =
        useState<boolean>(false)
    const [paymentMethod, setPaymentMethod] = useState<{
        method: 'card' | 'deposit'
        cardId: string | undefined
    }>({
        method: 'deposit',
        cardId: undefined
    })

    const { setPostTip } = useTipPost({
        onMutate: val => {
            onTipMutate(val)
        }
    })
    /*     const onTipMutate = (tipAmount: number) => {
        engagePost()
        updatePostTipCache(tipAmount)
    } */
    const cachedData =
        queryClient.getQueryData<InfiniteData<IInfinitePage>>(dataQuery)

    const onTipMutate = (tipAmount: number) => {
        // engagePost()
        if (cachedData) {
            const newData = {
                ...cachedData,
                pages: cachedData.pages.map(singleFeedPage => {
                    return {
                        ...singleFeedPage,
                        page: {
                            ...singleFeedPage.page,
                            data: {
                                ...singleFeedPage.page.data,
                                data: singleFeedPage.page.data.data.map(post =>
                                    post.goal && post.id === postData.id
                                        ? {
                                              ...post,
                                              goal: {
                                                  ...post.goal,
                                                  tipped:
                                                      post.goal?.tipped +
                                                      tipAmount
                                              }
                                          }
                                        : post
                                )
                            }
                        }
                    }
                })
            }

            queryClient.setQueryData(dataQuery, newData)
        }
    }

    const onMutateError = () => {
        queryClient.setQueryData(dataQuery, cachedData)
    }

    const onTipPost = (val: number) => {
        if (setPostTip.isLoading) {
            return
        }

        const payload: ITipPayload = {
            amount: val.toString(),
            post_id: postData.id,
            payment_method: paymentMethod.method,
            card_id: paymentMethod.cardId
        }

        // engagePost()
        setPostTip.mutate(payload, {
            onError: () => {
                onMutateError()
            },
            onSuccess: () => {
                setConfirmBuyModalOpen(false)
            },
            onSettled: () => {
                setConfirmBuyModalOpen(false)
            }
        })
    }

    const handleWrapperClick = (event: React.MouseEvent<HTMLDivElement>) => {
        // engagePost()
        const el = event.target as HTMLElement
        if (
            el.classList.contains('goalBox__options') ||
            el.classList.contains('goalBox__option')
        ) {
            return
        }
        if (previewData.minimized) {
            previewData.updateSelectedPost(
                postIndex,
                0,
                'goal',
                postData,
                dataQuery
            )
            previewData.handleMinimize(false)
        } else {
            previewData.addModal(postIndex, 0, 'goal', postData, dataQuery)
        }
    }

    const selectTipValue = (value: number) => {
        setSelectedTipValue(value)
    }

    const handleTip = () => {
        onTipPost(selectedTipValue)
    }

    // Same condition as elsewhere: no default card AND no wallet funds
    const noDefaultCard =
        userData?.default_card == null || String(userData?.default_card) === ''
    const noWalletDeposit =
        userData?.wallet_deposit == null || Number(userData?.wallet_deposit) <= 0
    const showAddCard = noDefaultCard && noWalletDeposit

    const goal = postData.goal

    const feedBackgroundSrc =
        goal?.background_src_thumb ||
        goal?.background_src ||
        ''

    const feedBackgroundDisplaySrc =
        feedBackgroundSrc
            ? getCachedBlob(feedBackgroundSrc) ??
            feedBackgroundSrc
            : ''


    return (
        <div
            className="post__files__file post__files__file--poll"
            onClick={handleWrapperClick}
            style={
                postData.goal?.background_color
                    ? {
                        background: getStoryBackground(
                            postData.goal.background_color
                        )
                    }
                    : undefined
            }
        >
            {feedBackgroundDisplaySrc && (
                <img
                    className="post__files__pollBg"
                    src={feedBackgroundDisplaySrc}
                    alt=""
                    draggable={false}
                />
            )}
            <PostGoal
                goalData={postData.goal}
                setTip={val => {
                    selectTipValue(val)
                    setConfirmBuyModalOpen(true)
                }}
                isMyPost={role === 'owner'}
                disabled={setPostTip.isLoading}
            />

            {confirmBuyModalOpen && (
                <Modal
                    onClose={() => setConfirmBuyModalOpen(false)}
                    title={showAddCard ? 'Add A Card' : 'Select payment method'}
                    closeOnLayerClick={true}
                    isLayerTransparent={false}
                >
                    {showAddCard ? (
                        <ConfirmModal
                            customClass="confirmModal--add-card"
                            confirmText="Add Card" // uses your updated ConfirmModal with confirmText
                            body={
                                <div>
                                    <p className="add-card-title-spacing">
                                        You don&apos;t have a saved card or wallet funds. Please add a card to continue.
                                    </p>
                                </div>
                            }
                            onCancel={() => setConfirmBuyModalOpen(false)}
                            confirmFn={() => {
                                navigate('/settings/general/wallet')
                                setConfirmBuyModalOpen(false)
                            }}
                        />
                    ) : (
                        <PaymentModal
                            purchaseCb={handleTip}
                            paymentMethod={paymentMethod}
                            setPaymentMethod={setPaymentMethod}
                            price={selectedTipValue}
                        />
                    )}
                </Modal>
            )}
        </div>
    )
}

export { PostGoalWrapper }